/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Component } from '@angular/core';
import { ConfirmationModel, ErrorResponseModel } from '../../models';
import { LoadingService, LogService, ModalService } from '../../services';
import { isObservable, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-confirm-box',
    templateUrl: './confirmBox.component.html',
    styles: ['app-modal-body { overflow-wrap: break-word; }'],
})

export class ConfirmBoxComponent {
    /**
     * Needed for subscribers as we are not extending the base component.
     * @protected
     */
    protected destroyed$: Subject<boolean> = new Subject();

    /**
     * The confirmation modal object
     */
    public confirmationObject: ConfirmationModel;

    constructor(
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService
    ) {
        this.setUpSubscribers();
        // Get a default confirmation object from the modal service.
        this.confirmationObject = this.modalService.confirmationObject;
    }

    /**
     * Submit observable action.
     */
    public confirmAction(): Observable<any> {
        return new Observable((observer) => {
            if (this.confirmationObject.callBack) {
                const unboundFunction = this.confirmationObject.callBack.apply(this.confirmationObject.callBack);
                if (unboundFunction && isObservable(unboundFunction)) {
                    this.logService.debug('ConfirmBoxComponent: confirmAction', 'Submitting Observable');
                    unboundFunction
                        .pipe(takeUntil(this.destroyed$))
                        .subscribe({
                            next: (response: any): void => {
                                observer.next(response);
                            },
                            error: (error: ErrorResponseModel): void => {
                                observer.error(error);
                            }
                        })
                        .add((): void => {
                            if (this.confirmationObject.closeInternally) {
                                this.loadingService.loadingButtonEmitter.next({
                                    id: 'confirmation-box',
                                    state: false
                                });
                                this.modalService.close('confirmation-box');
                                this.stopLoadingService();
                            }
                            observer.complete();
                        });
                } else {
                    this.logService.debug('ConfirmBoxComponent: confirmAction', 'Submitting normal function');
                    // Wrap logic in a timer, so it looks like something is happening.
                    const timer = setTimeout(() => {
                        this.modalService.close('confirmation-box');
                        this.stopLoadingService();
                        clearTimeout(timer);
                    }, 1500);
                }
            } else {
                this.modalService.close('confirmation-box');
                this.stopLoadingService();
            }
        });
    }

    /**
     * Cancel confirmation box.
     */
    public cancelAction(): void {
        this.stopLoadingService();
        this.modalService.cancelSubmission.next(true);
        this.modalService.close('confirmation-box');
        this.loadingService.loadingButtonEmitter.next(false);
    }

    /**
     * Set up subscriber to modal close
     * @private
     */
    private setUpSubscribers(): void {
        this.modalService.openParentModalEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (modal: any): void => {
                    if (modal.id === 'confirmation-box') {
                        this.confirmationObject = this.modalService.confirmationObject;
                    }
                }
            });

        this.modalService.closeParentModalEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (modal: any): void => {
                    if (modal.id === 'confirmation-box') {
                        this.stopLoadingService();
                    }
                }
            });
    }

    /**
     * Stop loading service
     * @private
     */
    private stopLoadingService(): void {
        this.loadingService.loadingActionDotsEmitter.next(false);
        this.loadingService.loadingButtonEmitter.next({ id: 'modal-confirm', state: false });
    }
}
